/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { Component, inject, ViewEncapsulation } from '@angular/core';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { AppStore, ReloadDocumentListAction } from '@alfresco/aca-shared/store';
import { Store } from '@ngrx/store';
import { NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { AddNoteService } from '../../../../services/add-note.service';

@Component({
  selector: 'app-add-note-dialog',
  templateUrl: './add-note-dialog.component.html',
  styleUrls: ['./add-note-dialog.component.scss'],
  imports: [NgIf, FormsModule, MatDialogModule, MatFormFieldModule, MatInputModule, MatButtonModule, MatIconModule],
  // `ViewEncapsulation.None` est impose par le depot, pour que les themes restent atteignables.
  // Corollaire : les regles de ce fichier sont GLOBALES. Sans la classe posee sur l'hote, la
  // surcharge du bouton principal — `mat-dialog-actions button[mat-flat-button]` — repeindrait le
  // bouton de toutes les boites de dialogue de l'application. Meme montage que les boites d'ACA.
  encapsulation: ViewEncapsulation.None,
  host: { class: 'alg-note-dialog' }
})
export class AddNoteDialogComponent {
  /**
   * Caracteres qu'Alfresco refuse dans un nom de noeud, pour l'affichage.
   *
   * Ils n'etaient pas verifies : un nom en contenant partait quand meme, le repository repondait
   * 422, et faute de chemin d'erreur la boite restait ouverte sans rien dire.
   */
  readonly forbiddenChars = '* " < > \\ / ? : |';

  /** Le meme jeu, sous forme de motif pour le champ. Voir `forbiddenChars`. */
  readonly namePattern = '^[^*"<>\\\\/?:|]+$';

  readonly maxNameLength = 150;

  nom = '';
  note = '';
  error = '';
  saving = false;

  readonly dialogRef = inject<MatDialogRef<AddNoteDialogComponent>>(MatDialogRef);
  private readonly store = inject<Store<AppStore>>(Store);
  private readonly addNoteService = inject(AddNoteService);

  get canSubmit(): boolean {
    const name = this.nom.trim();
    return !!name && !!this.note.trim() && new RegExp(this.namePattern).test(name);
  }

  onSubmit(): void {
    if (!this.canSubmit || this.saving) {
      return;
    }

    this.saving = true;
    this.error = '';

    this.addNoteService.addNote(this.baseName(), this.note).subscribe({
      next: () => {
        this.store.dispatch(new ReloadDocumentListAction());
        this.dialogRef.close(true);
      },
      error: (response: unknown) => {
        this.saving = false;
        this.error = this.messageFor(response);
      }
    });
  }

  /**
   * Nom sans son extension.
   *
   * Le service ajoute « .txt » au nom qu'on lui donne. Une note nommee « Compte rendu.txt »
   * devenait donc « Compte rendu.txt.txt ». Le champ annonce maintenant l'extension par un
   * suffixe, mais la saisir soi-meme reste un reflexe naturel : il ne doit pas etre puni.
   */
  private baseName(): string {
    const name = this.nom.trim();
    return name.replace(/\.txt$/i, '').trim() || name;
  }

  /**
   * Message lisible pour un echec de creation.
   *
   * L'appel n'avait AUCUN chemin d'erreur — `subscribe(() => ...)` ne traitait que le succes. Un
   * nom deja pris, le cas de loin le plus courant, laissait la boite ouverte, muette, son bouton
   * toujours actif : rien ne distinguait l'echec d'un clic non enregistre.
   */
  private messageFor(response: unknown): string {
    switch ((response as { status?: number })?.status) {
      case 409:
        return 'Une note porte déjà ce nom dans ce dossier.';
      case 403:
        return "Vous n'avez pas les droits pour créer une note ici.";
      case 422:
        return 'Ce nom est refusé par le serveur.';
      default:
        return "La note n'a pas pu être créée. Réessayez.";
    }
  }
}
